import { supabase } from './supabase'
import type { Badge, UserBadge } from '../types'

export interface BadgeWithProgress extends Badge {
  isEarned: boolean
  progress: number
  earnedAt?: string
}

export interface UserBadgeWithDetails extends UserBadge {
  badge: Badge
}

export class SupabaseBadgeService {
  /**
   * Get all badges with user's progress and earned status
   */
  async getAllBadgesWithProgress(userId: string): Promise<BadgeWithProgress[]> {
    // For now, use direct database queries instead of Edge Function
    // The Edge Function has authentication issues with the current Supabase client setup
    // TODO: Re-enable Edge Function once authentication is properly configured
    return this.getFallbackBadgesWithProgress(userId)
  }

  async getUserBadges(userId: string): Promise<UserBadgeWithDetails[]> {
    // For now, use direct database queries instead of Edge Function
    // The Edge Function has authentication issues with the current Supabase client setup
    return this.getFallbackUserBadges(userId)
  }

  async checkNewBadges(userId: string): Promise<{ newBadges: UserBadgeWithDetails[], count: number }> {
    try {
      // Call the Postgres function directly instead of going through Edge Function
      const { data, error } = await supabase.rpc('award_user_badges', {
        target_user_id: userId
      })

      if (error) {
        console.warn('Failed to check new badges:', error)
        return { newBadges: [], count: 0 }
      }

      // If badges were awarded, fetch the new badges
      if (data && data > 0) {
        const { data: newBadges } = await supabase
          .from('user_badges')
          .select(`
            id,
            user_id,
            badge_id,
            earned_at,
            progress,
            badges (*)
          `)
          .eq('user_id', userId)
          .order('earned_at', { ascending: false })
          .limit(data)

        const formattedBadges: UserBadgeWithDetails[] = (newBadges || []).map((ub: any) => ({
          id: ub.id,
          userId: ub.user_id,
          badgeId: ub.badge_id,
          earnedAt: ub.earned_at,
          progress: ub.progress,
          badge: {
            id: ub.badges?.id || '',
            name: ub.badges?.name || '',
            nameEnglish: ub.badges?.name_english || '',
            description: ub.badges?.description || '',
            descriptionEnglish: ub.badges?.description_english || '',
            icon: ub.badges?.icon || '',
            tier: ub.badges?.tier || 'bronze',
            category: ub.badges?.category || 'words',
            requiredValue: ub.badges?.required_value || 0,
            requirementType: ub.badges?.requirement_type || 'wordsSubmitted',
            isExclusive: ub.badges?.is_exclusive || false,
            maxAvailable: ub.badges?.max_available,
            earnedCount: ub.badges?.earned_count || 0
          }
        }))

        return {
          newBadges: formattedBadges,
          count: data
        }
      }

      return { newBadges: [], count: 0 }
    } catch (error) {
      console.warn('Failed to check new badges:', error)
      return { newBadges: [], count: 0 }
    }
  }

  /**
   * Fallback methods using database queries
   */
  private async getFallbackBadgesWithProgress(userId: string): Promise<BadgeWithProgress[]> {
    // Get user profile data
    const { data: profile } = await supabase
      .from('profiles')
      .select('words_submitted, alternatives_proposed, votes_cast')
      .eq('id', userId)
      .single()

    // Get all badge definitions from database
    const { data: allBadges } = await supabase
      .from('badges')
      .select('*')
      .order('required_value', { ascending: true })

    // Get earned badges from database
    const { data: userBadges } = await supabase
      .from('user_badges')
      .select('badge_id, earned_at')
      .eq('user_id', userId)

    const earnedBadgeIds = new Map(userBadges?.map(ub => [ub.badge_id, ub.earned_at]) || [])

    // Return badges with progress
    return (allBadges || []).map(badge => {
      const currentValue = this.getUserStatValue(profile, badge.requirement_type)
      const earnedAt = earnedBadgeIds.get(badge.id)

      return {
        id: badge.id,
        name: badge.name,
        nameEnglish: badge.name_english,
        description: badge.description,
        descriptionEnglish: badge.description_english,
        icon: badge.icon,
        tier: badge.tier as 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond',
        category: badge.category as 'words' | 'alternatives' | 'votes' | 'overall',
        requiredValue: badge.required_value,
        requirementType: badge.requirement_type as 'wordsSubmitted' | 'alternativesProposed' | 'votesCast' | 'totalScore',
        isExclusive: badge.is_exclusive,
        maxAvailable: badge.max_available,
        earnedCount: badge.earned_count,
        isEarned: !!earnedAt,
        progress: Math.min(currentValue, badge.required_value),
        earnedAt: earnedAt
      }
    })
  }

  private async getFallbackUserBadges(userId: string): Promise<UserBadgeWithDetails[]> {
    // Get earned badges with details from database
    const { data: userBadges } = await supabase
      .from('user_badges')
      .select(`
        id,
        user_id,
        badge_id,
        earned_at,
        progress,
        badges (*)
      `)
      .eq('user_id', userId)
      .order('earned_at', { ascending: false })

    return (userBadges || []).map((ub: any) => ({
      id: ub.id,
      userId: ub.user_id,
      badgeId: ub.badge_id,
      earnedAt: ub.earned_at,
      progress: ub.progress,
      badge: {
        id: ub.badges?.id || '',
        name: ub.badges?.name || '',
        nameEnglish: ub.badges?.name_english || '',
        description: ub.badges?.description || '',
        descriptionEnglish: ub.badges?.description_english || '',
        icon: ub.badges?.icon || '',
        tier: ub.badges?.tier || 'bronze',
        category: ub.badges?.category || 'words',
        requiredValue: ub.badges?.required_value || 0,
        requirementType: ub.badges?.requirement_type || 'wordsSubmitted',
        isExclusive: ub.badges?.is_exclusive || false,
        maxAvailable: ub.badges?.max_available,
        earnedCount: ub.badges?.earned_count || 0
      }
    }))
  }

  /**
   * Get user stat value from profile data (supports both snake_case and camelCase)
   */
  private getUserStatValue(user: any, statType: string): number {
    switch (statType) {
      case 'wordsSubmitted':
        return user.words_submitted || user.wordsSubmitted || 0
      case 'alternativesProposed':
        return user.alternatives_proposed || user.alternativesProposed || 0
      case 'votesCast':
        return user.votes_cast || user.votesCast || 0
      case 'totalScore':
        return user.total_score || user.totalScore || 0
      default:
        return 0
    }
  }
}

export const supabaseBadgeService = new SupabaseBadgeService()
